<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { applySession } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { durationLabel } from '../lib/format';
  import type { MaterialType, RecentItemDTO } from '../lib/types';

  let items = $state<RecentItemDTO[] | null>(null);
  let error = $state<string | null>(null);

  onMount(async () => {
    // Refresh session in background
    (async () => {
      try {
        if (isInsideTelegram()) applySession(await api.authMe(getInitData()));
        else applySession(await api.authSession());
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          replace(import.meta.env.DEV ? '/' : '/login');
        }
      }
    })();

    try {
      const { items: list } = await api.myRecent(50);
      items = list;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Не вдалось завантажити перегляди';
    }
  });
</script>

<div class="screen">
  <TopBar title="Останні переглянуті" backHref="/home" showLogo={false} />
  <div class="scroll">
    {#if error}
      <div class="empty">{error}</div>
    {:else if items && items.length === 0}
      <div class="empty-state">
        <div class="empty-icon"><Icon name="monitor-play" size="xl" /></div>
        <div class="empty-title">Ще немає переглядів</div>
        <div class="empty-text">Відкривай матеріали — історія переглядів з'явиться тут</div>
        <a class="empty-cta" href="#/catalog">Перейти до матеріалів →</a>
      </div>
    {:else if items}
      <div class="list card-list">
        {#each items as item (item.id)}
          <a class="material" href={`#/material/${item.id}`}>
            <div class="material-icon"><Icon name={MATERIAL_TYPE_ICONS[item.type]} /></div>
            <div class="material-text">
              <div class="material-title">{item.title}</div>
              <div class="material-meta">
                <span class="material-cat"><Icon name={item.category.icon} size="xs" /> {item.category.name}</span>
                {#if item.durationSec}
                  <span> · {durationLabel(item.durationSec)}</span>
                {/if}
              </div>
              {#if item.completedAt}
                <div class="material-progress-row done"><Icon name="check" size="xs" /> Переглянуто</div>
              {:else if item.maxProgressPct > 0}
                <div class="material-progress-row">
                  <span class="material-pct">{item.maxProgressPct}%</span>
                  <div class="material-progress">
                    <div class="material-progress-bar" style="width: {item.maxProgressPct}%"></div>
                  </div>
                </div>
              {/if}
            </div>
            <div class="material-chevron">›</div>
          </a>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px 24px;
  }
  .scroll::-webkit-scrollbar { width: 3px; }
  .scroll::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

  .material {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 8px;
    text-decoration: none;
    color: inherit;
  }
  .material-icon {
    font-size: 20px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    border-radius: 8px;
    flex-shrink: 0;
  }
  .material-text {
    flex: 1;
    min-width: 0;
  }
  .material-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .material-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .material-cat {
    color: var(--accent-text);
    font-weight: 600;
  }
  .material-progress-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 600;
  }
  .material-progress-row.done {
    color: var(--status-active);
  }
  .material-pct {
    color: var(--accent-text);
    flex-shrink: 0;
  }
  .material-progress {
    flex: 1;
    max-width: 50%;
    height: 2px;
    background: var(--border);
    border-radius: 999px;
    overflow: hidden;
  }
  .material-progress-bar {
    height: 100%;
    background: var(--pu);
    transition: width 0.3s var(--ease);
  }
  .material-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
  }

  .empty {
    text-align: center;
    padding: 40px 20px;
    color: var(--text-muted);
    font-size: 13px;
  }
  .empty-state {
    text-align: center;
    padding: 60px 24px;
  }
  .empty-icon {
    /* Розмір задає сама іконка (ic-xl = 30px) — контейнеру лишився відступ. */
    margin-bottom: 14px;
    color: var(--taupe);
  }
  .empty-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 6px;
  }
  .empty-text {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.5;
    margin-bottom: 24px;
  }
  .empty-cta {
    display: inline-block;
    background: var(--pu);
    color: var(--text-light);
    padding: 11px 22px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
  }
</style>
